import { useState } from "react";
import { PALETTE, FONT_DISPLAY, FONT_MONO, track } from "@/components/cobrain/tokens";

/* The deliverable itself, not a picture of it. A readable one-page audit
   excerpt the visitor can expand: ranked workflows, hours, approval rules
   and the explicit "don't build this yet" line. Anonymised composite. */

type Row = { task: string; payback: string; hours: string; tone: "high" | "mid" | "no" };

const ROWS: Row[] = [
  { task: "Missed-call callback + booking", payback: "Highest payback", hours: "~7 hrs/wk", tone: "high" },
  { task: "Quote follow-up sequence", payback: "High", hours: "~6 hrs/wk", tone: "high" },
  { task: "Recall / reminder outreach", payback: "Worth doing", hours: "~3 hrs/wk", tone: "mid" },
  { task: "Inbox triage + routing", payback: "Phase two", hours: "~2 hrs/wk", tone: "mid" },
  { task: "Full inventory sync rebuild", payback: "Not worth it", hours: "—", tone: "no" },
];

const TONE: Record<Row["tone"], { fg: string; bg: string }> = {
  high: { fg: "#1f6b4d", bg: "rgba(31,107,77,0.10)" },
  mid: { fg: "#8a5a12", bg: "rgba(196,130,58,0.14)" },
  no: { fg: "#7a4038", bg: "rgba(122,64,56,0.10)" },
};

const APPROVALS = [
  "Books appointments into open slots without asking.",
  "Drafts quote follow-ups — owner sends, for the first two weeks.",
  "Never quotes a price or discount. Escalates to the owner.",
  "Anything over $5k, a human calls back the same day.",
];

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: "0.14em", textTransform: "uppercase",
      color: PALETTE.muted, fontWeight: 700, marginBottom: 9,
    }}>
      {children}
    </div>
  );
}

export function AuditSample() {
  const [open, setOpen] = useState(false);

  return (
    <figure
      aria-label="Sample of the one-page plan an AI readiness audit hands over"
      style={{
        margin: 0, background: PALETTE.card, border: `1px solid ${PALETTE.line}`,
        borderRadius: 20, padding: "20px 20px 18px",
        boxShadow: "0 26px 60px -34px rgba(0,0,0,0.42)",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <span style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.muted }}>
          Audit output · sample
        </span>
        <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: PALETTE.muted }}>1 page</span>
      </div>
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 25, lineHeight: 1.15, margin: "6px 0 4px", color: PALETTE.ink }}>
        What to automate first
      </div>
      <p style={{ margin: "0 0 16px", fontSize: 12.5, color: PALETTE.muted, lineHeight: 1.5 }}>
        7-van home services business · 11 staff · anonymised
      </p>

      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 1, background: PALETTE.line, borderRadius: 12, overflow: "hidden" }}>
        {ROWS.map((r, i) => (
          <li
            key={r.task}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
              background: PALETTE.card, padding: "11px 13px",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: PALETTE.muted, flexShrink: 0 }}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 14, color: PALETTE.ink2, lineHeight: 1.35 }}>{r.task}</span>
                <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: PALETTE.muted }}>{r.hours}</span>
              </span>
            </span>
            <span style={{
              flexShrink: 0, fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: "0.08em",
              textTransform: "uppercase", padding: "4px 8px", borderRadius: 999,
              color: TONE[r.tone].fg, background: TONE[r.tone].bg,
              textDecoration: r.tone === "no" ? "line-through" : "none",
            }}>
              {r.payback}
            </span>
          </li>
        ))}
      </ul>

      {open && (
        <div className="svc-sample-more" style={{ marginTop: 16, display: "grid", gap: 16 }}>
          <div style={{ borderTop: `1px solid ${PALETTE.line}`, paddingTop: 16 }}>
            <Label>Approval rules — page 1, section 2</Label>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
              {APPROVALS.map((a) => (
                <li key={a} style={{ display: "flex", gap: 9, fontSize: 13.5, lineHeight: 1.5, color: PALETTE.ink2 }}>
                  <span aria-hidden style={{ color: PALETTE.amber, fontWeight: 700 }}>·</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
          <div style={{
            padding: "14px 15px", borderRadius: 14,
            background: "rgba(122,64,56,0.07)", border: "1px solid rgba(122,64,56,0.18)",
          }}>
            <Label>Don't build this yet</Label>
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: PALETTE.ink2 }}>
              The inventory sync touches three systems and saves one person about twenty minutes a day.
              Revisit it after the callback and quote workflows have run for a quarter — not before.
            </p>
          </div>
          <div style={{ borderTop: `1px solid ${PALETTE.line}`, paddingTop: 14 }}>
            <Label>Estimated recovery</Label>
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: PALETTE.ink2 }}>
              Roughly <strong style={{ color: PALETTE.ink }}>16 hours a week</strong> across the first two
              workflows, plus the after-hours calls nobody was returning. First one live in week one.
            </p>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => { setOpen((v) => !v); if (!open) track("services_sample_expand", {}); }}
        aria-expanded={open}
        style={{
          marginTop: 14, width: "100%", minHeight: 44, borderRadius: 999,
          border: `1px solid ${PALETTE.line}`, background: "transparent",
          color: PALETTE.ink2, fontSize: 13.5, fontWeight: 600, cursor: "pointer",
        }}
      >
        {open ? "Collapse the sample" : "Read the rest of the page →"}
      </button>

      <figcaption style={{ marginTop: 12, fontSize: 12.5, lineHeight: 1.5, color: PALETTE.muted }}>
        Every audit ends with a page like this — ranked by payback, including the things we tell you
        <em> not </em> to build. Once it's paid for, it's yours either way.
      </figcaption>
    </figure>
  );
}

export default AuditSample;
